/**
 * services/api.ts — CLI-450
 * Axios-инстанс кабинета пациента с автоматическим refresh токена.
 */
import axios, { type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { useAuthStore } from '../store/auth.store';

export const api = axios.create({ baseURL: '/api/patient' });

// Request interceptor: добавить access-токен
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// Response interceptor: при 401 — попробовать refresh
let isRefreshing = false;
let waitQueue: Array<(token: string) => void> = [];

api.interceptors.response.use(
  (r: AxiosResponse) => r,
  async (error: AxiosError) => {
    const originalConfig = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status !== 401 || originalConfig?._retry) {
      return Promise.reject(error);
    }

    const { refreshToken, setTokens, logout } = useAuthStore.getState();
    if (!refreshToken) {
      logout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        waitQueue.push((token: string) => {
          if (originalConfig) {
            originalConfig._retry = true;
            originalConfig.headers.set('Authorization', `Bearer ${token}`);
            resolve(api(originalConfig));
          }
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post<{ data: { accessToken: string; refreshToken: string } }>(
        '/api/patient/auth/refresh',
        { refreshToken },
      );
      const newToken = data.data.accessToken;
      const newRefreshToken = data.data.refreshToken;
      // Атомарно сохраняем ОБА токена — сервер ротирует refresh-токен
      setTokens(newToken, newRefreshToken);
      waitQueue.forEach((cb) => cb(newToken));
      waitQueue = [];

      if (originalConfig) {
        originalConfig._retry = true;
        originalConfig.headers.set('Authorization', `Bearer ${newToken}`);
        return api(originalConfig);
      }
    } catch {
      logout();
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }

    return Promise.reject(error);
  },
);
